import type { IUnassigned } from "@services/types/sections/sectionDataTypes";
import type { AssignedIntervals, UnassignedSessionRow } from "./types";

/**
 * Multiplies the weekly block count by all distinct assigned weeks, assuming
 * the same count in each week. Preserves zero; returns null for missing counts
 * or incomplete assignment data.
 */
export const getUnassignedTotalBlocks = (
  blocksCount: IUnassigned["blocksCount"],
  intervals: AssignedIntervals,
): number | null => {
  if (blocksCount == null || !intervals || intervals.some(interval => !interval?.id)) {
    return null;
  }

  return blocksCount * new Set(intervals.map(interval => interval.id)).size;
};

/**
 * Selects sessions for the current week while keeping their full-session totals.
 * Any unknown row total makes the aggregate unknown (null).
 */
export const getUnassignedSessionsSummary = (
  sessions: IUnassigned[] = [],
  selectedWeekId?: string,
) => {
  const rows: UnassignedSessionRow[] = [];

  if (selectedWeekId) {
    sessions.forEach(session => {
      const intervals: AssignedIntervals = session.resource?.intervals;
      const interval = intervals?.find(week => week?.id === selectedWeekId);

      if (interval) {
        rows.push({
          session,
          interval,
          totalBlocks: getUnassignedTotalBlocks(session.blocksCount, intervals),
        });
      }
    });
  }

  const totalBlocks = rows.reduce<number | null>((total, row) => {
    if (total === null || row.totalBlocks === null) return null;
    return total + row.totalBlocks;
  }, 0);

  return { rows, totalBlocks };
};
